<template>
  <div class="codex-container">
    <div v-if="loading" class="codex-loading">
      <div class="loading-spinner-large"></div>
      <p class="loading-text">正在加载 Codex 配额信息...</p>
    </div>

    <template v-else>
      <!-- Codex 公告（顶部） -->
      <div v-if="notice" class="codex-notice box">
        <iframe
          :srcdoc="noticeHtml"
          sandbox="allow-same-origin"
          scrolling="no"
          class="codex-notice-iframe"
          @load="resizeNoticeIframe"
          ref="noticeIframeRef"
        ></iframe>
      </div>

      <!-- 顶部配额卡片 -->
      <div class="codex-card box">
        <!-- 未开通提示：没有过期时间 -->
        <div v-if="!hasExpireTime" class="codex-empty">
          <div class="codex-empty-icon">🔒</div>
          <p class="codex-empty-msg">暂无 Codex 授权信息</p>
          <p class="codex-empty-sub">请联系管理员开通 Codex 使用权限</p>
        </div>

        <!-- 已过期提示 -->
        <div v-else-if="isExpired" class="codex-empty expired">
          <div class="codex-empty-icon">⏰</div>
          <p class="codex-empty-msg">Codex 订阅已过期</p>
          <p class="codex-empty-sub">到期时间：{{ formatDate(sub.expireTime) }}，请联系管理员续费</p>
        </div>

        <!-- 已授权且未过期 -->
        <template v-else>
          <div class="api-credentials-row">
            <!-- Base URL 展示 -->
            <div class="api-key-card base-url">
              <div class="api-key-label">
                <span class="api-key-dot"></span>
                Base URL
              </div>
              <div class="api-key-body">
                <code class="api-key-text">{{ apiBaseUrl }}</code>
                <div class="api-key-actions">
                  <button class="key-action-btn primary" title="复制" @click="copyBaseUrl">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    复制
                  </button>
                </div>
              </div>
            </div>

            <!-- API Key 单独展示 -->
            <div class="api-key-card">
              <div class="api-key-label">
                <span class="api-key-dot"></span>
                API Key
              </div>
              <div class="api-key-body">
                <code class="api-key-text">{{ showKey ? codex.apiKey : maskedKey }}</code>
                <div class="api-key-actions">
                  <button class="key-action-btn" :title="showKey ? '隐藏' : '显示'" @click="showKey = !showKey">
                    <svg v-if="!showKey" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    {{ showKey ? '隐藏' : '显示' }}
                  </button>
                  <button class="key-action-btn primary" title="复制" @click="copyKey">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    复制
                  </button>
                  <button class="key-action-btn danger" title="重置" @click="openResetModal" :disabled="resetting">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                    重置
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="codex-info-grid">
            <div class="info-item">
              <div class="info-label">套餐</div>
              <div class="info-value">{{ sub && sub.subTypeName || '—' }}</div>
            </div>

            <div class="info-item">
              <div class="info-label">计费类型</div>
              <div class="info-value">{{ billingLabel }}</div>
            </div>

            <div class="info-item">
              <div class="info-label">周期剩余</div>
              <div class="info-value">
                <span class="quota-num">{{ remainingText }}</span>
                <span class="quota-total"> / {{ limitText }}</span>
              </div>
            </div>

            <div class="info-item">
              <div class="info-label">周期</div>
              <div class="info-value">{{ periodLabel }}</div>
            </div>
          </div>

          <!-- 进度条 -->
          <div v-if="hasQuotaLimit" class="codex-progress-wrap" :title="`已使用 ${usagePercent}%`">
            <div class="codex-progress">
              <div class="codex-progress-bar" :style="{ width: usagePercent + '%' }"></div>
            </div>
            <div class="codex-progress-text">已使用 {{ usagePercent }}%</div>
          </div>

          <!-- 累计使用 -->
          <div class="codex-stats">
            <div class="stat-box">
              <div class="stat-num">{{ currentUsage && currentUsage.totalRequests || 0 }}</div>
              <div class="stat-label">总请求数</div>
            </div>
            <div class="stat-box">
              <div class="stat-num">{{ currentUsage && currentUsage.totalTokens || 0 }}</div>
              <div class="stat-label">总 Tokens</div>
            </div>
            <div class="stat-box">
              <div class="stat-num">${{ (currentUsage && currentUsage.totalCost || 0).toFixed(4) }}</div>
              <div class="stat-label">累计费用</div>
            </div>
          </div>
        </template>
      </div>

      <!-- 中部提示 Banner -->
      <div class="codex-banner-group">
        <div class="codex-banner warn">
          <div class="codex-banner-icon">⚠️</div>
          <div class="codex-banner-body">
            <div class="codex-banner-title">不建议无编程经验的用户使用</div>
            <div class="codex-banner-text">Codex 需要在本地终端/命令行中运行，涉及环境配置与脚本执行，整体流程相对繁琐，请量力而行。</div>
          </div>
        </div>
      </div>

      <!-- 安装脚本卡片（仅在有有效授权时展示） -->
      <div v-if="hasExpireTime && !isExpired" class="codex-card box">
        <h3 class="section-title">
          <span class="title-icon">⚡</span>
          一键安装脚本
        </h3>

        <div class="platform-tabs">
          <button
            v-for="tab in platformTabs"
            :key="tab.key"
            class="platform-tab"
            :class="{ active: currentPlatform === tab.key }"
            @click="currentPlatform = tab.key"
          >
            {{ tab.name }}
          </button>
          <a
            class="tutorial-link"
            :href="windowsTutorialUrl"
            target="_blank"
            rel="noopener noreferrer"
            title="打开 Windows 安装教程（PDF），新标签页"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Windows 安装教程
          </a>
        </div>

        <div class="script-block">
          <div class="script-header">
            <span class="script-label">{{ currentScript.label }}</span>
            <button class="copy-btn" @click="copyScript">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              {{ copyBtnText }}
            </button>
          </div>
          <pre class="script-content"><code>{{ currentScript.cmd }}</code></pre>
          <div class="script-tip">
            <span class="tip-icon">💡</span>
            <span>{{ currentScript.tip }}</span>
          </div>
        </div>
      </div>

    </template>

    <!-- 重置 API Key 确认弹窗 -->
    <div v-if="showResetModal" class="reset-modal-overlay" @click="closeResetModal">
      <div class="reset-modal" @click.stop>
        <button class="reset-close" @click="closeResetModal" :disabled="resetting">×</button>
        <div class="reset-icon-wrap">
          <div class="reset-icon">⚠</div>
        </div>
        <h3 class="reset-title">重置 API Key</h3>
        <p class="reset-desc">您正在重置 Codex 的 API Key</p>
        <ul class="reset-warn-list">
          <li>原有的 API Key 将立即失效</li>
          <li>所有使用该 Key 的应用需要更新配置</li>
          <li>此操作不可撤销</li>
        </ul>
        <div class="reset-actions">
          <button class="reset-btn cancel" @click="closeResetModal" :disabled="resetting">取消</button>
          <button class="reset-btn confirm" @click="confirmReset" :disabled="resetting">
            <span v-if="!resetting">确认重置</span>
            <span v-else class="reset-loading">
              <span class="reset-spinner"></span>
              重置中...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import toast from '../utils/toast'
import { resetVibeCodeKey } from '../api/home'

export default {
  name: 'CodexView',
  props: {
    loading: { type: Boolean, default: false },
    data: { type: Object, default: null },
    notice: { type: String, default: '' }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const showKey = ref(false)
    const currentPlatform = ref('mac')
    const copyBtnText = ref('复制')
    const showResetModal = ref(false)
    const resetting = ref(false)
    const noticeIframeRef = ref(null)

    const noticeHtml = computed(() => {
      return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>*{margin:0;padding:0;box-sizing:border-box}html,body{font-family:"KaiTi",serif;font-weight:700;color:#222;text-align:center;line-height:1.6;overflow:hidden}img{max-width:100%;height:auto}a{color:#7b5cff;text-decoration:underline}</style></head><body>${props.notice || ''}</body></html>`
    })

    const resizeNoticeIframe = () => {
      const iframe = noticeIframeRef.value
      if (!iframe) return
      try {
        iframe.style.height = '0px'
        const h = iframe.contentDocument.documentElement.scrollHeight
        iframe.style.height = h + 'px'
      } catch (e) { /* cross-origin */ }
    }

    watch(() => props.notice, () => {
      nextTick(resizeNoticeIframe)
    })

    const codex = computed(() => props.data && props.data.codex)
    const sub = computed(() => codex.value && codex.value.subscriptions)
    const currentUsage = computed(() => codex.value && codex.value.currentUsage)

    const hasExpireTime = computed(() => {
      const t = sub.value && sub.value.expireTime
      return !!t
    })

    const isExpired = computed(() => {
      const t = sub.value && sub.value.expireTime
      if (!t) return false
      const d = new Date(t)
      if (isNaN(d.getTime())) return false
      return d.getTime() < Date.now()
    })

    const maskedKey = computed(() => {
      const k = codex.value && codex.value.apiKey
      if (!k) return '—'
      if (k.length <= 10) return '*'.repeat(k.length)
      return k.slice(0, 6) + '*'.repeat(Math.max(4, k.length - 10)) + k.slice(-4)
    })

    const billingLabel = computed(() => {
      const t = sub.value && sub.value.billingType
      if (t === 'count') return '按次数'
      if (t === 'amount') return '按金额'
      if (t === 'token') return '按 Token'
      return t || '—'
    })

    const periodLabel = computed(() => {
      const p = sub.value && sub.value.period
      if (!p) return '—'
      const m = String(p).match(/^(\d+)\s*([hdwmy])$/i)
      if (!m) return p
      const n = m[1]
      const unit = { h: '小时', d: '天', w: '周', m: '月', y: '年' }[m[2].toLowerCase()]
      return `${n} ${unit}`
    })

    const remainingText = computed(() => {
      if (!sub.value) return '0'
      if (sub.value.billingType === 'amount') return `$${(sub.value.remainingAmount || 0).toFixed(2)}`
      return String(sub.value.remainingCount || 0)
    })

    const limitText = computed(() => {
      if (!sub.value) return '0'
      if (sub.value.billingType === 'amount') return `$${(sub.value.amountLimit || 0).toFixed(2)}`
      return String(sub.value.limit || 0)
    })

    const hasQuotaLimit = computed(() => {
      if (!sub.value) return false
      if (sub.value.billingType === 'amount') return Number(sub.value.amountLimit) > 0
      return Number(sub.value.limit) > 0
    })

    const usagePercent = computed(() => {
      if (!sub.value) return 0
      let used, total
      if (sub.value.billingType === 'amount') {
        total = Number(sub.value.amountLimit) || 0
        used = Number(sub.value.usedAmount) || 0
      } else {
        total = Number(sub.value.limit) || 0
        used = Number(sub.value.usedCount) || 0
      }
      if (!total) return 0
      const pct = (used / total) * 100
      if (pct > 0 && pct < 1) return Math.ceil(pct * 100) / 100
      return Math.min(100, Math.round(pct))
    })

    const formatDate = (iso) => {
      if (!iso) return '—'
      try {
        const d = new Date(iso)
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        return `${y}-${m}-${day} ${hh}:${mm}`
      } catch (e) {
        return iso
      }
    }

    const copyKey = async () => {
      const k = codex.value && codex.value.apiKey
      if (!k) return
      try {
        await navigator.clipboard.writeText(k)
        toast.success('API Key 已复制')
      } catch (e) {
        toast.error('复制失败')
      }
    }

    const platformTabs = [
      { key: 'mac', name: 'macOS / Linux' },
      { key: 'win', name: 'Windows' }
    ]

    const baseUrl = computed(() => {
      return window.location.origin + '/codex'
    })

    const apiBaseUrl = computed(() => {
      return window.location.origin + '/codex/v1'
    })

    const windowsTutorialUrl = computed(() => {
      return window.location.origin + '/codex-install-script/ShareAI-Windows-Cli-下载教程.pdf'
    })

    const copyBaseUrl = async () => {
      try {
        await navigator.clipboard.writeText(apiBaseUrl.value)
        toast.success('Base URL 已复制')
      } catch (e) {
        toast.error('复制失败')
      }
    }

    const scripts = computed(() => {
      const key = (codex.value && codex.value.apiKey) || ''
      const url = baseUrl.value
      return {
        mac: {
          label: 'macOS / Linux',
          cmd: `curl -s ${window.location.origin}/codex-install-script/linux-setup-codex.sh | bash -s -- --url ${url} --key ${key}`,
          tip: '本脚本会自动检测系统环境，安装必要的依赖，并完成环境变量的配置。脚本执行成功后，请重新打开终端，开始使用。'
        },
        win: {
          label: 'Windows (PowerShell)',
          cmd: `& { $url='${url}'; $key='${key}'; iwr -useb ${window.location.origin}/codex-install-script/windows-setup-codex.ps1 | iex }`,
          tip: '本脚本会自动检测系统环境，安装必要的依赖，并完成环境变量的配置。脚本执行成功后，请重新打开 PowerShell，开始使用。如果提示无权限，请以管理员身份运行【Set-ExecutionPolicy -ExecutionPolicy RemoteSigned -Scope CurrentUser -Force】，并重启 PowerShell 开始使用。'
        }
      }
    })

    const currentScript = computed(() => scripts.value[currentPlatform.value])

    const copyScript = async () => {
      try {
        await navigator.clipboard.writeText(currentScript.value.cmd)
        copyBtnText.value = '已复制'
        toast.success('安装脚本已复制')
        setTimeout(() => { copyBtnText.value = '复制' }, 2000)
      } catch (e) {
        toast.error('复制失败')
      }
    }

    const openResetModal = () => {
      showResetModal.value = true
    }

    const closeResetModal = () => {
      if (resetting.value) return
      showResetModal.value = false
    }

    const confirmReset = async () => {
      if (resetting.value) return
      resetting.value = true
      try {
        const res = await resetVibeCodeKey('codex')
        if (res && res.code === 1) {
          toast.success('API Key 已重置')
          showResetModal.value = false
          emit('refresh')
        } else {
          toast.error((res && res.msg) || '重置失败')
        }
      } catch (e) {
        toast.error('网络错误，请重试')
      } finally {
        resetting.value = false
      }
    }

    return {
      showKey,
      currentPlatform,
      copyBtnText,
      showResetModal,
      resetting,
      codex,
      sub,
      currentUsage,
      hasExpireTime,
      isExpired,
      maskedKey,
      billingLabel,
      periodLabel,
      remainingText,
      limitText,
      hasQuotaLimit,
      usagePercent,
      formatDate,
      copyKey,
      apiBaseUrl,
      windowsTutorialUrl,
      copyBaseUrl,
      platformTabs,
      currentScript,
      copyScript,
      noticeIframeRef,
      noticeHtml,
      resizeNoticeIframe,
      openResetModal,
      closeResetModal,
      confirmReset
    }
  }
}
</script>

<style scoped>
.codex-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px;
}

.codex-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
}
.codex-loading .loading-spinner-large {
  width: 60px; height: 60px;
  border: 4px solid rgba(123, 92, 255, 0.1);
  border-radius: 50%;
  border-top-color: #7b5cff;
  animation: spin 1s linear infinite;
}
.codex-loading .loading-text {
  font-size: 16px; color: #666; font-weight: 600;
}

.codex-card {
  padding: 22px 24px;
}

/* 顶部提示 Banner */
.codex-banner-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.codex-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid;
  font-size: 13px;
  line-height: 1.65;
}
.codex-banner-icon {
  flex-shrink: 0;
  font-size: 18px;
  line-height: 1.4;
}
.codex-banner-body { flex: 1; min-width: 0; }
.codex-banner-title {
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 2px;
}
.codex-banner-text { font-weight: 500; }
.codex-banner-text strong { font-weight: 800; }

.codex-banner.warn {
  background: linear-gradient(135deg, #fff7e6 0%, #fffaf0 100%);
  border-color: #ffd591;
  color: #8a5a00;
}
.codex-banner.warn .codex-banner-title { color: #a66300; }

.codex-banner.info {
  background: linear-gradient(135deg, #eef4ff 0%, #f3efff 100%);
  border-color: #c7d5ff;
  color: #3a4a80;
}
.codex-banner.info .codex-banner-title { color: #3a40b0; }
.codex-banner.info .codex-banner-text strong { color: #7b5cff; }

.codex-empty {
  padding: 50px 20px;
  text-align: center;
}
.codex-empty-icon { font-size: 48px; margin-bottom: 12px; }
.codex-empty-msg { font-size: 16px; font-weight: 700; color: #333; margin: 0 0 6px; }
.codex-empty-sub { font-size: 13px; color: #888; margin: 0; }
.codex-empty.expired .codex-empty-msg { color: #e0803b; }

.codex-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.info-item {
  background: #fafafe;
  border: 1px solid #f0f0f5;
  border-radius: 12px;
  padding: 12px 14px;
}

.info-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
  font-weight: 600;
  letter-spacing: .3px;
}

.info-value {
  font-size: 14px;
  font-weight: 700;
  color: #1f1f23;
  word-break: break-all;
}

.api-credentials-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
  gap: 10px;
  margin-bottom: 14px;
}
.api-key-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f8f7ff 0%, #eef4ff 100%);
  border: 1px solid #e6e3ff;
  border-radius: 12px;
  padding: 8px 12px;
  min-width: 0;
}
.api-key-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #7b5cff;
  font-weight: 700;
  letter-spacing: .4px;
  text-transform: uppercase;
  flex-shrink: 0;
  min-width: 72px;
}
.api-key-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #7b5cff;
  box-shadow: 0 0 0 3px rgba(123, 92, 255, 0.15);
}
.api-key-body {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.api-key-text {
  flex: 1;
  min-width: 0;
  display: inline-block;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #ece9ff;
  border-radius: 6px;
  font-family: "JetBrains Mono", "SFMono-Regular", "Consolas", monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: #3d3a66;
  letter-spacing: .3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}
.api-key-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.key-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 10px;
  border: 1px solid #dcd6ff;
  border-radius: 6px;
  background: #fff;
  color: #6b5ecf;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
  white-space: nowrap;
}
.key-action-btn:hover {
  background: #f3efff;
  border-color: #b7a8ff;
  color: #7b5cff;
}
.key-action-btn.primary {
  background: linear-gradient(135deg, #7b5cff, #9d7bff);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 6px rgba(123, 92, 255, 0.3);
}
.key-action-btn.primary:hover {
  background: linear-gradient(135deg, #6a4aff, #8c68ff);
  color: #fff;
}
.key-action-btn.danger {
  border-color: #ffd1cf;
  color: #e5484d;
  background: #fff5f5;
}
.key-action-btn.danger:hover:not(:disabled) {
  background: #ffe5e4;
  border-color: #ff9c98;
  color: #c42b30;
}
.key-action-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}

/* 重置 API Key 确认弹窗 */
.reset-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn .25s ease;
}
.reset-modal {
  position: relative;
  width: 92%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 26px 22px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp .25s ease;
}
.reset-close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  border-radius: 50%;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all .2s;
}
.reset-close:hover:not(:disabled) {
  background: rgba(229, 72, 77, 0.1);
  color: #e5484d;
}
.reset-close:disabled { opacity: .5; cursor: not-allowed; }

.reset-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}
.reset-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffe5e4, #fff0b8);
  color: #e5484d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  box-shadow: 0 6px 16px rgba(229, 72, 77, 0.2);
}

.reset-title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #1f1f23;
  margin: 6px 0 4px;
}
.reset-desc {
  text-align: center;
  font-size: 14px;
  color: #555;
  margin: 0 0 14px;
  font-weight: 600;
}
.reset-warn-list {
  margin: 0 0 22px;
  padding: 12px 16px 12px 32px;
  background: #fff7f7;
  border: 1px solid #ffe0de;
  border-radius: 10px;
  color: #b3373b;
  font-size: 13px;
  line-height: 1.9;
}
.reset-warn-list li { padding: 0; }

.reset-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.reset-btn {
  min-width: 92px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all .25s;
  border: 1px solid transparent;
}
.reset-btn.cancel {
  background: #fff;
  color: #555;
  border-color: #d9d9d9;
}
.reset-btn.cancel:hover:not(:disabled) {
  color: #222;
  border-color: #999;
}
.reset-btn.confirm {
  background: linear-gradient(135deg, #ff5a5f, #e5484d);
  color: #fff;
  box-shadow: 0 4px 12px rgba(229, 72, 77, 0.35);
}
.reset-btn.confirm:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(229, 72, 77, 0.45);
}
.reset-btn:disabled { opacity: .6; cursor: not-allowed; }

.reset-loading { display: inline-flex; align-items: center; gap: 6px; }
.reset-spinner {
  width: 12px; height: 12px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .9s linear infinite;
}

@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px) scale(.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.quota-num { color: #7b5cff; font-size: 16px; }
.quota-total { color: #999; font-weight: 600; font-size: 13px; }

.codex-progress-wrap {
  margin: 6px 0 18px;
  cursor: pointer;
}
.codex-progress {
  width: 100%;
  height: 10px;
  background: #f0f0f5;
  border-radius: 999px;
  overflow: hidden;
  transition: height .2s;
}
.codex-progress-wrap:hover .codex-progress { height: 12px; }
.codex-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #7b5cff, #00dceb);
  transition: width .8s cubic-bezier(.4,0,.2,1);
  border-radius: 999px;
}
.codex-progress-text {
  font-size: 12px;
  color: #7b5cff;
  margin-top: 6px;
  font-weight: 600;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: opacity .25s, max-height .25s;
}
.codex-progress-wrap:hover .codex-progress-text {
  opacity: 1;
  max-height: 20px;
}

.codex-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 8px;
}
.stat-box {
  background: linear-gradient(135deg, #f6f4ff, #eef7ff);
  border-radius: 12px;
  padding: 14px 10px;
  text-align: center;
}
.stat-num {
  font-size: 20px;
  font-weight: 800;
  color: #7b5cff;
  margin-bottom: 2px;
}
.stat-label {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 800;
  color: #1f1f23;
  margin: 0 0 14px;
}
.title-icon { font-size: 18px; }

.platform-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  align-items: center;
  flex-wrap: wrap;
}
.tutorial-link {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 7px 14px;
  border: 2px solid #ffd591;
  border-radius: 10px;
  background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
  color: #a66300;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  transition: all .25s;
  margin-left: 4px;
}
.tutorial-link:hover {
  border-color: #ffa940;
  color: #d46b08;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255, 169, 64, .25);
}
.tutorial-link svg {
  flex-shrink: 0;
}
.platform-tab {
  padding: 7px 16px;
  border: 2px solid #ececf0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  transition: all .25s;
}
.platform-tab:hover { border-color: #7b5cff; color: #7b5cff; }
.platform-tab.active {
  background: linear-gradient(141deg, rgba(191,102,255,1) 0%, rgba(98,72,255,1) 50%, rgba(0,221,235,1) 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(123, 92, 255, 0.3);
}

.script-block {
  background: #f7f8fc;
  border: 1px solid #e8eaf2;
  border-radius: 12px;
  overflow: hidden;
}
.script-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #eef0f7;
  color: #4a5070;
  font-size: 12px;
  font-weight: 700;
  border-bottom: 1px solid #e3e6f0;
}
.script-label { letter-spacing: .4px; }
.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #d6dae8;
  border-radius: 6px;
  background: #fff;
  color: #555b7a;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
}
.copy-btn:hover { background: #f3efff; border-color: #7b5cff; color: #7b5cff; }

.script-content {
  margin: 0;
  padding: 14px 16px;
  color: #2d3148;
  font-family: "JetBrains Mono", "SFMono-Regular", "Consolas", monospace;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;
  background: #f7f8fc;
}

.script-tip {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  background: #fffbe8;
  color: #8a6d1e;
  font-size: 12px;
  line-height: 1.6;
  border-top: 1px solid #f5edc8;
}
.tip-icon { flex-shrink: 0; }

/* Codex 公告 */
.codex-notice {
  padding: 16px 20px;
}
.codex-notice-iframe {
  width: 100%;
  height: 0;
  border: none;
  display: block;
  background: transparent;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .codex-card { padding: 16px 14px; }
  .codex-info-grid { grid-template-columns: 1fr; }
  .codex-stats { grid-template-columns: 1fr; }
  .script-content { font-size: 12px; }
  .api-credentials-row { grid-template-columns: 1fr; }
  .api-key-card { flex-direction: column; align-items: stretch; gap: 6px; padding: 10px 12px; }
  .api-key-label { min-width: 0; }
  .api-key-body { flex-wrap: wrap; }
  .api-key-actions { flex-wrap: wrap; }
}
</style>
